import { ReactNode, SyntheticEvent } from "react";

import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { get, kebabCase } from "lodash";

import EnumDataSource from "utils/enum-data-source.util";

import StatusIconContainer from "./status-icon-container.component";
import StatusIconPlaceholder from "./status-icon-placeholder.component";
import {
  DisabledStatusTransitionCode,
  StatusTransitionCode
} from "./status-transition-cell.component";

type StatusTransitionMenuProps = {
  testId: string;
  status: StatusTransitionCode;
  statusColors?: Record<StatusTransitionCode, string>;
  statusIcons?: Record<StatusTransitionCode, ReactNode>;
  statuses: EnumDataSource;
  availableTransitions: StatusTransitionCode[];
  disabledTransitions: DisabledStatusTransitionCode | undefined;
  anchorEl: null | HTMLElement;
  onChange?: (code: StatusTransitionCode) => void;
  onClose: () => void;
};
export default function StatusTransitionMenu({
  testId,
  status,
  statusColors,
  statusIcons,
  statuses,
  availableTransitions,
  disabledTransitions,
  anchorEl,
  onChange,
  onClose
}: StatusTransitionMenuProps) {
  const theme = useTheme();
  return (
    <Menu
      data-testid={`${testId}-menu`}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={(e: SyntheticEvent) => {
        onClose();
        e?.stopPropagation();
      }}
      sx={{ zIndex: theme.zIndex.snackbar + 2 }}
      MenuListProps={{ sx: { minWidth: 180 } }}
    >
      {availableTransitions.map((statusValue, index) => {
        const statusIcon = get(statusIcons, statusValue, undefined);
        const statusColor = get(statusColors, statusValue, undefined);
        const statusDisabledText = get(
          disabledTransitions,
          statusValue,
          undefined
        );

        const placement =
          index === 0
            ? "top"
            : availableTransitions.length - 1 === index
              ? "bottom"
              : "left";

        return (
          <Tooltip
            key={statusValue}
            title={statusDisabledText ? statusDisabledText : ""}
            placement={placement}
          >
            <MenuItem
              disabled={Boolean(statusDisabledText)}
              data-testid={`${testId}-menu-item-${kebabCase(
                statuses.getFromCode(statusValue)
              )}`}
              value={statusValue}
              selected={statusValue === status}
              sx={{
                height: 42,
                "&.Mui-disabled": { pointerEvents: "all" },
                ...(!!statusDisabledText
                  ? { "&:hover": { background: "transparent" } }
                  : {})
              }}
              onClick={(e) => {
                if (statusDisabledText) return;
                onChange?.(statusValue);
                onClose();
                e?.stopPropagation();
              }}
            >
              <Stack gap={1} direction="row" alignItems="center">
                {statusIcon ? (
                  <StatusIconContainer color={statusColor}>
                    {statusIcon}
                  </StatusIconContainer>
                ) : (
                  <Box>
                    {statusColor && (
                      <StatusIconPlaceholder color={statusColor} />
                    )}
                  </Box>
                )}
                <Typography variant="body1" sx={{ textTransform: "uppercase" }}>
                  {statuses.getFromCode(statusValue)}
                </Typography>
              </Stack>
            </MenuItem>
          </Tooltip>
        );
      })}
    </Menu>
  );
}
